import React, { useCallback } from 'react'
import { View, StyleSheet } from 'react-native'
import { useRouter } from 'expo-router'
import { useQuery } from 'convex/react'

import { api } from '@ppwr/convex/_generated/api'
import { useGameContext } from '../GameContext'
import { useGameState } from '../useGameState'
import { DS } from '../../../design-system'
import { getLeaveGameStatsLines } from '../hooks/useLeaveGameStatsMessage'
import { translate } from '../../../i18n'
import { GameModal } from './GameModalContainer'

type ExitDestination = 'play' | 'home' | 'lounge'

interface QuickGameOverModalProps {
  visible: boolean
  onClose: () => void
  onViewStats: () => void
  /** Where the secondary CTA navigates. Defaults to lounge (if any) or play. */
  exitDestination?: ExitDestination
}

export const QuickGameOverModal = ({
  visible,
  onClose,
  onViewStats,
  exitDestination
}: QuickGameOverModalProps) => {
  const router = useRouter()
  const { gameId, userId } = useGameContext()
  const { game } = useGameState()
  const playerStats = useQuery(
    api.playerStats.queries.getPlayerGameStats,
    gameId && userId ? { gameId, userId } : 'skip'
  )

  const resolvedExit: ExitDestination =
    exitDestination ?? (game?.loungeId ? 'lounge' : 'play')

  const handleExit = useCallback(() => {
    if (resolvedExit === 'home') {
      router.dismissTo('/(root)/(tabs)/home')
      return
    }
    if (resolvedExit === 'lounge' && game?.loungeId) {
      router.dismissTo(`/(root)/lounges/${game.loungeId}` as never)
      return
    }
    router.dismissTo('/(root)/(tabs)/play')
  }, [router, resolvedExit, game?.loungeId])

  const exitLabel =
    resolvedExit === 'home'
      ? translate('gameScreen.quickGameOver.goToHome')
      : resolvedExit === 'lounge'
        ? translate('gameScreen.quickGameOver.goToLounge')
        : translate('gameScreen.quickGameOver.goToPlay')

  const statsLines = getLeaveGameStatsLines({
    numberOfHands: playerStats?.numberOfHands,
    numberOfHandsWon: playerStats?.numberOfHandsWon,
    totalChipsWon: playerStats?.totalChipsWon,
    totalChipsLost: playerStats?.totalChipsLost,
    rank: playerStats?.userStats?.rank ?? null
  })
  const leaveGameStatsMessage = statsLines.join('\n')

  return (
    <GameModal visible={visible} onClose={onClose}>
      <View style={styles.modalContainer}>
        <DS.Frame style={styles.container}>
          <DS.Icon name="Stars" width={48} height={48} color="Brand/Maroon" />
          <DS.Text variant="Heading 2/Medium" textAlign="center">
            {translate('gameScreen.quickGameOver.title')}
          </DS.Text>
          <DS.Text variant="Body Medium/Regular" color="Brand/Dark Grey" textAlign="center">
            {translate('gameScreen.quickGameOver.message')}
          </DS.Text>
          {leaveGameStatsMessage && (
            <DS.Text variant="Body Medium/Regular" color="Brand/Dark Grey" textAlign="center">
              {leaveGameStatsMessage}
            </DS.Text>
          )}
          <DS.Frame style={styles.buttonsContainer}>
            <DS.Button variant="Large/Primary" type="On Light" onPress={onViewStats} style={{ marginTop: 16 }}>
              {translate('gameScreen.quickGameOver.viewStats')}
            </DS.Button>
            <DS.Button variant="Large/Secondary" type="On Dark" onPress={handleExit}>
              {exitLabel}
            </DS.Button>
          </DS.Frame>
        </DS.Frame>
      </View>
    </GameModal>
  )
}

const styles = StyleSheet.create({
  modalContainer: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center'
  },
  container: {
    backgroundColor: DS.palette['Brand/Cream'],
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    gap: 12,
    width: '100%',
    maxWidth: 340
  },
  buttonsContainer: {
    width: '100%',
    gap: 12
  }
})
